import { ApolloError, gql } from '@apollo/client';
import { useMutation } from 'platform/useMutation';
import { useObservability } from 'shared/helpers/observability';

import {
  ClearLastSSVARecordingByRecordedFeature,
  ClearLastSSVARecordingByRecordedFeatureVariables,
} from 'sellDirect/graphql/__generated__/ClearLastSSVARecordingByRecordedFeature';
import {
  UpdateSSVARecordings,
  UpdateSSVARecordingsVariables,
} from 'sellDirect/graphql/__generated__/UpdateSSVARecordings';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import {
  OdProtosWebData_RecordedFeature,
  OdProtosWebData_VirtualAssessmentRecordingInput,
} from '__generated__/globalTypes';
import { VirtualAssessmentQuery } from 'sellDirect/graphql/useVirtualAssessmentData';
import { getHubOverviewRoute } from 'shared/helpers/routes';
import { generatePath } from 'react-router-dom';

const UPDATE_SSVA_RECORDINGS = gql`
  mutation UpdateSSVARecordings(
    $sellerInputUUID: String!
    $ssvaRecordings: [OdProtosWebData_VirtualAssessmentRecordingInput!]
  ) {
    sellDirect {
      updateSSVARecordings(
        input: {
          recordings: $ssvaRecordings
          lead: { source: { type: RECEPTION, id: $sellerInputUUID } }
        }
      ) {
        virtualAssessment {
          id
          ssvaRecordingsSubmitted
          ssvaQuestionnaireSubmitted
          ssvaInteriorRecordingsSubmitted
          ssvaFrontyardRecordingsSubmitted
          ssvaBackyardRecordingsSubmitted
          ssvaRecordings {
            id
            recordedFeature
            s3Directory
            fileName
          }
        }
      }
    }
  }
`;

// TODO: Move this graphql call to its own file
const CLEAR_SSVA_RECORDING_BY_RECORDED_FEATURE = gql`
  mutation ClearLastSSVARecordingByRecordedFeature(
    $sellerInputUUID: String!
    $recordedFeature: OdProtosWebData_RecordedFeature!
    $clearAll: Boolean
  ) {
    sellDirect {
      clearLastSSVARecordingByRecordedFeature(
        input: {
          recordedFeature: $recordedFeature
          lead: { source: { type: RECEPTION, id: $sellerInputUUID } }
          clearAll: $clearAll
        }
      ) {
        deleted
      }
    }
  }
`;

/** TODO: Add documentation */
export const useUpdateVirtualAssessmentRecordings = () => {
  const { sellerInputUUID, addressUUID } = useQualificationContext();
  const { trackError } = useObservability();

  const onError = (error: ApolloError) => {
    if (error) {
      trackError('update-virtual-assessment', error);
      window.location.assign(generatePath(getHubOverviewRoute(), { addressUUID }));
    }
  };

  const [updateWebMutation, mutationResp] = useMutation<
    UpdateSSVARecordings,
    UpdateSSVARecordingsVariables
  >(UPDATE_SSVA_RECORDINGS, {
    onError,
    awaitRefetchQueries: true,
    fetchPolicy: 'no-cache',
    refetchQueries: [{ query: VirtualAssessmentQuery, variables: { sellerInputUUID } }],
  });

  const [clearWebMutation, clearMutationResp] = useMutation<
    ClearLastSSVARecordingByRecordedFeature,
    ClearLastSSVARecordingByRecordedFeatureVariables
  >(CLEAR_SSVA_RECORDING_BY_RECORDED_FEATURE, {
    onError,
    awaitRefetchQueries: true,
    fetchPolicy: 'no-cache',
    refetchQueries: [{ query: VirtualAssessmentQuery, variables: { sellerInputUUID } }],
  });

  const updateSSVARecordings = (
    ssvaRecordings: Array<OdProtosWebData_VirtualAssessmentRecordingInput>,
  ) =>
    updateWebMutation({
      variables: { sellerInputUUID, ssvaRecordings },
    }).catch(onError);

  const clearLastSSVARecordingByFeature = (
    feature: OdProtosWebData_RecordedFeature,
    clearAll = false,
  ) => {
    return clearWebMutation({
      variables: { sellerInputUUID, recordedFeature: feature, clearAll },
    }).catch(onError);
  };

  return {
    updateSSVARecordings,
    clearLastSSVARecordingByFeature,
    ssvaRecordings:
      mutationResp.data?.sellDirect.updateSSVARecordings?.virtualAssessment?.ssvaRecordings ?? [],
    loading: mutationResp.loading,
    error: mutationResp.error,
    clearing: clearMutationResp.loading,
  };
};
